import { BodyPrimary, H1 } from '@liveauctioneers/hammer-ui-core/text';

import TopBadge from '@/components/Badges/TopBadge';
import CatalogStatus from '@/components/CatalogComponent/components/CatalogStatus/CatalogStatus';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsDone,
    getCatalogSellerId,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getSellerLogoId, getSellerName } from '@/redux/modules/seller/seller.selectors';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyRender } from '@/utils/safelyRender';
import { getAuctioneerLogoUrl, getSellerUrl } from '@/utils/urls';

const AUCTION_HOUSE_LOGO_SIZE_PX = 42;
const AUCTION_HOUSE_LOGO_SOURCE_SIZE_PX = 84;

type Props = {
    catalogId: number;
    forceIsLive: boolean;
    setForceIsLive: (force: boolean) => void;
};

// Figma's mobile "H1-Mobile" style (24px/36px/Jost) is hui-text-h1's own <1023px breakpoint
// value — the same breakpoint useWindowIsTabletOrMobile() uses elsewhere in the codebase to gate
// mobile-only components (e.g. AuctionDetailsMobile in ItemPageMain.tsx). Once this component is
// wired up behind that same gate, the H1 component will always render at the mobile size here.
export const CatalogDetailsZoneMobileHeader = ({ catalogId, forceIsLive, setForceIsLive }: Props) => {
    const formatMessage = useAppFormatMessage();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getSellerName(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const showSellerLogo = Boolean(logoId) && !whiteLabel;
    const sellerUrl = getSellerUrl(sellerId, sellerName);

    return (
        <div className="flex flex-col gap-sm">
            <div className="flex flex-col gap-sm">
                <H1 className="w-full">{typeof title === 'string' ? safelyRender({ html: title }) : title}</H1>
                <div className="flex items-center gap-xs">
                    {showSellerLogo && (
                        <RouteLink
                            className="flex shrink-0 items-center justify-center border border-border-secondary"
                            data-testid="details-zone-seller-logo"
                            style={{ height: AUCTION_HOUSE_LOGO_SIZE_PX, width: AUCTION_HOUSE_LOGO_SIZE_PX }}
                            to={sellerUrl}
                        >
                            <img
                                alt={sellerName}
                                className="size-full object-contain"
                                height={AUCTION_HOUSE_LOGO_SIZE_PX}
                                src={getAuctioneerLogoUrl(logoId, AUCTION_HOUSE_LOGO_SOURCE_SIZE_PX)}
                                width={AUCTION_HOUSE_LOGO_SIZE_PX}
                            />
                        </RouteLink>
                    )}
                    <div className="flex flex-col gap-2xs">
                        {sellerIsTopRated && <TopBadge iconSize="small" />}
                        <RouteLink
                            className="no-underline"
                            to={sellerUrl}
                        >
                            <BodyPrimary color="secondary">
                                {formatMessage(['catalogPage', 'catalogDetailsZone', 'by'], {
                                    values: {
                                        sellerName: (
                                            <span
                                                className="font-semibold text-text-primary"
                                                key="seller-name"
                                            >
                                                {sellerName}
                                            </span>
                                        ),
                                    },
                                })}
                            </BodyPrimary>
                        </RouteLink>
                    </div>
                </div>
            </div>
            {!isCatalogDone && (
                <CatalogStatus
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                    layout="inline"
                    setForceIsLive={setForceIsLive}
                />
            )}
        </div>
    );
};
